import { Button } from './Button';
import { FileText, Plus, RefreshCw, Database, Search, Filter } from 'lucide-react';

interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: 'document' | 'plus' | 'refresh' | 'database' | 'search' | 'filter';
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'outline';
    icon?: 'document' | 'plus' | 'refresh' | 'database' | 'search' | 'filter';
  };
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const iconMap = {
  document: FileText,
  plus: Plus,
  refresh: RefreshCw,
  database: Database,
  search: Search,
  filter: Filter
};

const sizeClasses = {
  sm: {
    container: 'h-32 p-4',
    icon: 'h-8 w-8',
    title: 'text-sm font-medium',
    description: 'text-xs',
    button: 'text-xs px-3 py-1'
  },
  md: {
    container: 'h-48 p-6',
    icon: 'h-10 w-10',
    title: 'text-base font-medium',
    description: 'text-sm',
    button: 'text-sm px-4 py-2'
  },
  lg: {
    container: 'h-64 p-8',
    icon: 'h-12 w-12',
    title: 'text-lg font-medium',
    description: 'text-base',
    button: 'text-base px-6 py-3'
  }
};

/**
 * Composant EmptyState réutilisable pour afficher un état vide
 * Conçu pour s'intégrer dans les structures existantes (tableaux, cartes, etc.)
 * sans remplacer toute la page
 */
export function EmptyState({ 
  title, 
  description, 
  icon = 'document', 
  action, 
  size = 'md',
  className = '' 
}: EmptyStateProps) {
  const IconComponent = iconMap[icon];
  const classes = sizeClasses[size];

  return (
    <div className={`flex flex-col items-center justify-center text-center ${classes.container} ${className}`}>
      <div className="text-muted-foreground/50 mb-3">
        <IconComponent className={`${classes.icon} mx-auto`} />
      </div>
      
      <h3 className={`${classes.title} text-foreground mb-2`}>
        {title}
      </h3>
      
      {description && (
        <p className={`${classes.description} text-muted-foreground mb-4 max-w-xs`}>
          {description}
        </p>
      )}
      
      {action && (
        <Button 
          onClick={action.onClick}
          variant={action.variant || 'primary'}
          className="flex items-center gap-2"
        >
          {(() => {
            const ActionIcon = action.icon ? iconMap[action.icon] : Plus;
            return <ActionIcon className="h-4 w-4" />;
          })()}
          {action.label}
        </Button>
      )}
    </div>
  );
}

/**
 * Composant spécialisé pour les tableaux vides
 * Remplace le contenu du tbody tout en gardant la structure de la table
 */
interface EmptyTableStateProps {
  title: string;
  description?: string;
  colSpan: number;
  icon?: EmptyStateProps['icon'];
  action?: EmptyStateProps['action'];
}

export function EmptyTableState({ 
  title, 
  description, 
  colSpan, 
  icon = 'database',
  action 
}: EmptyTableStateProps) {
  return (
    <tr>
        <td colSpan={colSpan} className="px-6 py-10">
        <EmptyState
          title={title}
          description={description}
          icon={icon}
          action={action}
          size="md"
          className="h-auto"
        />
      </td>
    </tr>
  );
}

/**
 * Composant de skeleton pour les tableaux pendant le chargement
 */
interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export function TableSkeleton({ rows = 5, columns = 4 }: TableSkeletonProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className="animate-pulse">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="px-6 py-4">
              <div className="h-4 bg-muted rounded w-full"></div>
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

/**
 * Composant de skeleton pour les cartes
 */
interface CardSkeletonProps {
  lines?: number;
  className?: string;
}

export function CardSkeleton({ lines = 3, className = '' }: CardSkeletonProps) {
  return (
    <div className={`animate-pulse space-y-3 p-4 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div key={index} className="space-y-2">
          <div className="h-4 bg-muted rounded w-full"></div>
          {index === 0 && (
            <div className="h-4 bg-muted rounded w-3/4"></div>
          )}
        </div>
      ))}
    </div>
  );
}